'use client';
import PropTypes from 'prop-types';
import axios from 'axios';
import { SWRConfig, unstable_serialize } from 'swr';
import React, { createContext, useContext } from 'react';
import { useEnvVars } from '@web/app/contexts/EnvContext';
import { shared } from '../../config';
import { BASE, SESSION_KEY } from './constants';
import { useSingleton } from './container';

class Cache extends Map {
  constructor(initialSession) {
    /** @type {[string, import('swr').State][]} */
    const data = [];
    // pre-populate the cache even when initialSession is null (unauthenticated user case)
    // so SWR knows the session was already checked server-side and doesn't treat
    // tab-focus revalidation as a new fetch
    if (initialSession !== undefined) {
      data.push([
        unstable_serialize(SESSION_KEY),
        {
          error: undefined,
          isValidating: false,
          isLoading: false,
          data: initialSession,
        },
      ]);
    }

    super(data);
  }
}

const ClientContext = createContext(null);

export const useClient = () => useContext(ClientContext);

export const ClientProvider = ({
  initialSession,
  swrConfig = {},
  children,
}) => {
  const cache = useSingleton(Cache, () => new Cache(initialSession));
  const appEnvVars = useEnvVars();
  const client = useSingleton('client', () => {
    let client;
    if (appEnvVars) {
      client = axios.create({
        baseURL: appEnvVars.OLLIE_ENV === 'test' ? '' : BASE(appEnvVars),
      });
    } else {
      client = axios.create({
        baseURL: shared.OLLIE_ENV === 'test' ? '' : BASE(appEnvVars),
      });
    }

    client.interceptors.request.use(request => {
      const cachedData = cache.get(unstable_serialize(SESSION_KEY));
      const token = cachedData?.data?.user?.access_token;

      // the shopify_user flag is added during auth to help
      // differentiate between Legacy and Shopify auth flows
      // note that the nested user is the profile that contains details
      // from Shopify such as the email, etc.
      const isShopifyUser = cachedData?.data?.user?.user?.shopify_user;
      const shopifyId = cachedData?.data?.user?.shopify_id;

      if (token) {
        request.headers['Authorization'] = `Bearer ${token}`;

        // if the token is a Shopify Access Token and shopifyId exists, send it
        // to the BE. Without a shopifyId, Shopify requests will be rejected as
        // its used to validate the token
        if (shopifyId && !!isShopifyUser) {
          request.headers['x-ollie-user-id'] = `${shopifyId}`;
        }
      }
      request.headers['X-Referer'] =
        typeof window !== 'undefined' ? window.location.href : 'https://node';
      return request;
    });
    return client;
  });
  const fetcher = useSingleton(
    'fetcher',
    () => url => client.get(url).then(res => res.data),
  );
  return (
    <SWRConfig
      value={{
        fetcher,
        shouldRetryOnError: false,
        revalidateOnFocus: false,
        provider: () => cache,
        ...swrConfig,
      }}
    >
      <ClientContext.Provider value={client}>{children}</ClientContext.Provider>
    </SWRConfig>
  );
};

ClientProvider.propTypes = {
  initialSession: PropTypes.object,
  swrConfig: PropTypes.object,
  children: PropTypes.node,
};
